import { useState, useCallback, useRef } from 'react';
import { validateField } from './validation';
import { FieldValidation, FieldType } from './types';

export const useFormValidation = (fieldConfigs: FieldValidation[]) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const validate = useCallback((fieldType: FieldType, value: string) => {
    const config = fieldConfigs.find(f => f.fieldType === fieldType);
    if (!config) return { isValid: true };

    const result = validateField(
      value,
      config.rules,
      config.fieldName,
      config.customMessage,
      config.validationParams
    );

    setErrors(prev => ({
      ...prev,
      [fieldType]: result.isValid ? '' : result.errorMessage || ''
    }));

    return result;
  }, [fieldConfigs]);

  const refs = fieldConfigs.reduce((acc, config) => {
    acc[config.fieldType] = (element: HTMLElement | null) => {
      fieldRefs.current[config.fieldType] = element;
    };
    return acc;
  }, {} as Record<string, (element: HTMLElement | null) => void>);

  const handleBlur = useCallback((fieldType: FieldType, value: string) => {
    setTouched(prev => ({ ...prev, [fieldType]: true }));
    return validate(fieldType, value);
  }, [validate]);

  const handleChange = useCallback((fieldType: FieldType, value: string) => {
    const config = fieldConfigs.find(f => f.fieldType === fieldType);
    if (config?.validateOnChange) {
      if (fieldType === 'autocompleteAddress') {
        setTouched(prev => ({ ...prev, [fieldType]: true }));
      }
      return validate(fieldType, value);
    }

    if (touched[fieldType] && errors[fieldType]) {
      return validate(fieldType, value);
    }
    return { isValid: true };
  }, [fieldConfigs, touched, errors, validate]);

  const focusFirstInvalidField = useCallback((currentErrors?: Record<FieldType, string>) => {
    const errorsToCheck = currentErrors || errors;
    for (const config of fieldConfigs) {
      if (errorsToCheck[config.fieldType]) {
        const element = fieldRefs.current[config.fieldType];
        if (element) {
          if (element.querySelector('input[type="radio"]')) {
            (element?.querySelector('input[type="radio"]') as HTMLInputElement)?.focus();
          }
          else {
            element.focus();
          }
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
  }, [fieldConfigs, errors]);

  const validateAll = useCallback((formData: Record<string, string>) => {
    const results: Record<string, boolean> = {};
    const currentErrors: Record<string, string> = {};

    fieldConfigs.forEach(config => {
      const result = validate(config.fieldType, formData[config.fieldType] || '');
      results[config.fieldType] = result.isValid;
      if (!result.isValid) {
        currentErrors[config.fieldType] = result.errorMessage || '';
      }
      setTouched(prev => ({ ...prev, [config.fieldType]: true }));
    });

    const isFormValid = Object.values(results).every(Boolean);
    if (!isFormValid) {
      setTimeout(() => focusFirstInvalidField(currentErrors), 0);
    }

    return isFormValid;
  }, [fieldConfigs, validate, focusFirstInvalidField]);

  const getFieldError = useCallback((fieldType: FieldType) => {
    return touched[fieldType] ? errors[fieldType] : '';
  }, [touched, errors]);

  const hasFieldError = useCallback((fieldType: FieldType) => {
    return touched[fieldType] && !!errors[fieldType];
  }, [touched, errors]);

  return {
    handleBlur,
    handleChange,
    validateAll,
    getFieldError,
    hasFieldError,
    refs,
    errors,
    touched
  };
};